<script lang="ts">
    import { onMount } from 'svelte';
    import { api } from '../../framework/js/api.js';
    import { toast } from '../../framework/js/toast.js';
    import { spinner } from '../../framework/js/spinner.js';

    interface ActivityLog {
        uuid: string;
        event_type: string;
        user_uuid: string | null;
        user: { uuid: string; first_name: string; last_name: string; email: string } | null;
        related_entity_type: string | null;
        related_entity_uuid: string | null;
        metadata: Record<string, unknown>;
        ip_address: string | null;
        created_at: string;
    }

    const EVENT_TYPE_LABELS: Record<string, string> = {
        'user_signup': 'User Signup',
        'user_login': 'User Login',
        'user_onboarding_completed': 'Onboarding Completed',
        'brief_started': 'Built-to-Brief Started',
        'brief_generated': 'Built-to-Brief Generated',
        'illuminator_started': 'Illuminator Started',
        'illuminator_generated': 'Illuminator Generated',
        'gapfinder_started': 'GapFinder Started',
        'gapfinder_generated': 'GapFinder Generated',
        'research_library_upload': 'Research Library Upload',
        'research_library_delete': 'Research Library Delete',
        'plan_change': 'Plan Change',
        'credit_purchase': 'Credit Purchase'
    };

    const EVENT_TYPE_COLORS: Record<string, string> = {
        'user_signup': 'var(--status-success)',
        'user_login': 'var(--primary)',
        'user_onboarding_completed': 'var(--status-success)',
        'brief_started': 'var(--accent-orange)',
        'brief_generated': 'var(--accent-orange-ink)',
        'illuminator_started': 'var(--accent-teal)',
        'illuminator_generated': 'var(--accent-teal-ink)',
        'gapfinder_started': 'var(--accent-pink)',
        'gapfinder_generated': 'var(--accent-pink-ink)',
        'research_library_upload': 'var(--status-info)',
        'research_library_delete': 'var(--status-danger)',
        'plan_change': 'var(--status-warn)',
        'credit_purchase': 'var(--status-success)'
    };

    const EVENT_TYPE_OPTIONS = [
        { value: '', label: 'All Event Types' },
        ...Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => ({ value, label }))
    ];

    let items: ActivityLog[] = [];
    let isLoading = false;
    let fromDate = '';
    let toDate = '';
    let userFilter = '';
    let eventTypeFilter = '';
    let selectedActivity: ActivityLog | null = null;
    let showDetailModal = false;
    let paging = { current_page: 0, last_page: 1, per_page: 20, total: 0, from: 0, to: 0 };
    let currentPage = 1;

    // User search for filter
    let userSearchQuery = '';
    let userSearchResults: Array<{ uuid: string; first_name: string; last_name: string; email: string }> = [];
    let selectedUser: { uuid: string; first_name: string; last_name: string; email: string } | null = null;
    let showUserDropdown = false;
    let userSearchDebounceTimer: ReturnType<typeof setTimeout>;

    onMount(async () => {
        await loadActivityLogs();
    });

    async function loadActivityLogs() {
        isLoading = true;
        spinner.show();
        try {
            const params: Record<string, string | number> = {
                page: currentPage,
                per_page: 20
            };
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;
            if (userFilter) params.user_uuid = userFilter;
            if (eventTypeFilter) params.event_type = eventTypeFilter;

            const response: any = await api.getActivityLogs(params);
            if (response.success) {
                items = response.items || [];
                paging = response.paging || paging;
            } else {
                toast.showNotification(false, response.message || 'Failed to load activity log');
                items = [];
            }
        } catch (error) {
            toast.showNotification(false, 'Error loading activity log');
            console.error(error);
            items = [];
        } finally {
            spinner.hide();
            isLoading = false;
        }
    }

    async function searchUsers() {
        if (!userSearchQuery || userSearchQuery.length < 2) {
            userSearchResults = [];
            return;
        }
        try {
            const response: any = await api.makeRequest('GET', `/api/v1/user?phrase=${encodeURIComponent(userSearchQuery)}&per_page=20`);
            if (response.success && response.data) {
                userSearchResults = Array.isArray(response.data) ? response.data : [response.data];
            } else {
                userSearchResults = [];
            }
        } catch {
            userSearchResults = [];
        }
    }

    function debounceUserSearch() {
        clearTimeout(userSearchDebounceTimer);
        userSearchDebounceTimer = setTimeout(() => {
            searchUsers();
        }, 300);
    }

    function selectUser(user: { uuid: string; first_name: string; last_name: string; email: string }) {
        selectedUser = user;
        userFilter = user.uuid;
        userSearchQuery = '';
        userSearchResults = [];
        showUserDropdown = false;
        currentPage = 1;
        loadActivityLogs();
    }

    function clearUserFilter() {
        selectedUser = null;
        userFilter = '';
        currentPage = 1;
        loadActivityLogs();
    }

    function applyFilters() {
        currentPage = 1;
        loadActivityLogs();
    }

    function clearFilters() {
        fromDate = '';
        toDate = '';
        userFilter = '';
        selectedUser = null;
        eventTypeFilter = '';
        currentPage = 1;
        loadActivityLogs();
    }

    function formatDate(dateString: string): string {
        return new Date(dateString).toLocaleString(navigator.language, {
            dateStyle: 'medium',
            timeStyle: 'short',
        });
    }

    function getEventLabel(eventType: string): string {
        return EVENT_TYPE_LABELS[eventType] || eventType;
    }

    function getEventColor(eventType: string): string {
        return EVENT_TYPE_COLORS[eventType] || 'var(--grey-secondary)';
    }

    function getSummary(activity: ActivityLog): string {
        const m = activity.metadata || {};
        switch (activity.event_type) {
            case 'user_signup':
                return `${m.email || 'Unknown'} signed up`;
            case 'user_login':
                return `${m.email || 'Unknown'} logged in`;
            case 'user_onboarding_completed':
                return `${m.email || 'Unknown'} completed onboarding`;
            case 'brief_started':
                return `Built-to-Brief "${m.brief_name || 'Untitled'}" started`;
            case 'brief_generated':
                return `Built-to-Brief version ${m.version_number || '?'} generated`;
            case 'illuminator_started':
                return `Illuminator "${m.assessment_name || 'Untitled'}" started`;
            case 'illuminator_generated':
                return `Illuminator "${m.assessment_name || 'Untitled'}" generated${m.verdict_label ? ` (${m.verdict_label})` : ''}`;
            case 'gapfinder_started':
                return `GapFinder "${m.analysis_name || 'Untitled'}" started`;
            case 'gapfinder_generated':
                return `GapFinder "${m.analysis_name || 'Untitled'}" generated${m.overall_status ? ` (${m.overall_status})` : ''}`;
            case 'research_library_upload':
                return `Uploaded "${m.file_name || 'file'}"`;
            case 'research_library_delete':
                return `Deleted "${m.file_name || 'file'}"`;
            case 'plan_change':
                return `${m.change_type || 'Change'} - ${m.plan_name || ''}`;
            case 'credit_purchase':
                return `Credit purchase: ${typeof m.amount === 'number' ? m.amount : m.amount || '?'}`;
            default:
                return JSON.stringify(m).slice(0, 80);
        }
    }

    function openDetail(activity: ActivityLog) {
        selectedActivity = activity;
        showDetailModal = true;
    }

    function closeDetailModal() {
        showDetailModal = false;
        selectedActivity = null;
    }

    function handleKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            closeDetailModal();
            showUserDropdown = false;
        }
    }

    function handleClickOutside(event: MouseEvent) {
        const target = event.target as HTMLElement;
        if (showUserDropdown && !target.closest('.user-filter')) {
            showUserDropdown = false;
        }
    }

    function getRelatedEntityLink(activity: ActivityLog): string | null {
        if (!activity.related_entity_uuid) return null;
        if (activity.related_entity_type === 'brief') return `/#/admin/briefs/${activity.related_entity_uuid}`;
        if (activity.related_entity_type === 'assessment') return `/#/assessments/${activity.related_entity_uuid}`;
        if (activity.related_entity_type === 'gapfinder') return `/#/gapfinder/${activity.related_entity_uuid}`;
        if (activity.related_entity_type === 'order') return null;
        return null;
    }

    function goToPage(page: number) {
        currentPage = page;
        loadActivityLogs();
    }

    $: hasFilters = fromDate || toDate || userFilter || eventTypeFilter;
</script>

<svelte:window on:keydown={handleKeydown} on:click={handleClickOutside} />

<div class="page-shell admin-list">
    <div class="page-header">
        <div class="header-content">
            <h2 class="page-title">Activity Log</h2>
            <p class="page-subtitle">View major system activity across all users</p>
        </div>
        <div class="header-stats">
            <span class="stat-badge">{paging.total} event{paging.total !== 1 ? 's' : ''}</span>
        </div>
    </div>

    <!-- Filters -->
    <div class="filters-bar filters-bar--labeled">
        <div class="filter-group">
            <label for="from-date" class="filter-label">From</label>
            <input
                id="from-date"
                type="date"
                bind:value={fromDate}
                on:change={applyFilters}
                class="filter-input date-input"
            />
        </div>
        <div class="filter-group">
            <label for="to-date" class="filter-label">To</label>
            <input
                id="to-date"
                type="date"
                bind:value={toDate}
                on:change={applyFilters}
                class="filter-input date-input"
            />
        </div>
        <div class="filter-group user-filter">
            <label for="user-search" class="filter-label">User</label>
            <div class="user-select-wrapper">
                {#if selectedUser}
                    <div class="selected-user">
                        <span>{selectedUser.first_name} {selectedUser.last_name} ({selectedUser.email})</span>
                        <button type="button" class="clear-user-btn" on:click={clearUserFilter} aria-label="Clear user filter">×</button>
                    </div>
                {:else}
                    <input
                        id="user-search"
                        type="text"
                        placeholder="Search user by name or email..."
                        bind:value={userSearchQuery}
                        on:input={debounceUserSearch}
                        on:focus={() => showUserDropdown = true}
                        class="filter-input"
                    />
                    {#if showUserDropdown && userSearchResults.length > 0}
                        <ul class="user-dropdown" role="listbox">
                            {#each userSearchResults as user (user.uuid)}
                                <li role="option" aria-selected="false" tabindex="0" on:click={() => selectUser(user)} on:keydown={(e) => e.key === 'Enter' && selectUser(user)}>
                                    {user.first_name} {user.last_name} - {user.email}
                                </li>
                            {/each}
                        </ul>
                    {/if}
                {/if}
            </div>
        </div>
        <div class="filter-group">
            <label for="event-type" class="filter-label">Event Type</label>
            <select id="event-type" bind:value={eventTypeFilter} on:change={applyFilters} class="filter-select">
                {#each EVENT_TYPE_OPTIONS as option}
                    <option value={option.value}>{option.label}</option>
                {/each}
            </select>
        </div>
        {#if hasFilters}
            <button type="button" class="clear-filters-btn" on:click={clearFilters}>Clear filters</button>
        {/if}
        <button type="button" class="refresh-btn" on:click={loadActivityLogs} disabled={isLoading} title="Refresh" aria-label="Refresh">
            <i class="refresh icon" aria-hidden="true"></i>
        </button>
        <span class="results-count">
            {paging.total} {paging.total === 1 ? 'event' : 'events'}
        </span>
    </div>

    {#if isLoading}
        <div class="loading-state">Loading activity...</div>
    {:else if items.length === 0}
        <div class="empty-state">
            <i class="history icon empty-icon" aria-hidden="true"></i>
            <p>No activity found.</p>
            {#if hasFilters}
                <button type="button" class="clear-filters-btn" on:click={clearFilters}>Clear filters</button>
            {/if}
        </div>
    {:else}
        <div class="table-wrap">
            <table class="data-table">
                <thead>
                    <tr>
                        <th><i class="clock icon" aria-hidden="true"></i> Timestamp</th>
                        <th><i class="tag icon" aria-hidden="true"></i> Event Type</th>
                        <th><i class="user icon" aria-hidden="true"></i> User</th>
                        <th><i class="align left icon" aria-hidden="true"></i> Summary</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {#each items as activity (activity.uuid)}
                        <tr class="clickable-row data-row" on:click={() => openDetail(activity)} role="button" tabindex="0" on:keydown={(e) => e.key === 'Enter' && openDetail(activity)}>
                            <td class="date-cell">{formatDate(activity.created_at)}</td>
                            <td>
                                <span class="color-badge" style="--badge-color: {getEventColor(activity.event_type)}">{getEventLabel(activity.event_type)}</span>
                            </td>
                            <td class="user-cell">
                                {#if activity.user}
                                    <div class="user-name">{activity.user.first_name} {activity.user.last_name}</div>
                                    <div class="user-email">{activity.user.email}</div>
                                {:else}
                                    <span class="text-muted">—</span>
                                {/if}
                            </td>
                            <td class="summary-cell">{getSummary(activity)}</td>
                            <td class="actions-cell">
                                <button type="button" class="view-button" on:click|stopPropagation={() => openDetail(activity)}>
                                    <i class="icon eye"></i> View
                                </button>
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        {#if paging.last_page > 1}
            <div class="pagination">
                <span class="pagination-info">
                    Showing {paging.from || 0}–{paging.to || 0} of {paging.total}
                </span>
                <div class="pagination-buttons">
                    <button type="button" class="pagination-btn" disabled={currentPage <= 1} on:click={() => goToPage(currentPage - 1)} aria-label="Previous page">Previous</button>
                    <span class="page-indicator">Page {currentPage} of {paging.last_page}</span>
                    <button type="button" class="pagination-btn" disabled={currentPage >= paging.last_page} on:click={() => goToPage(currentPage + 1)} aria-label="Next page">Next</button>
                </div>
            </div>
        {/if}
    {/if}
</div>

{#if showDetailModal && selectedActivity}
    <div
        class="modal-overlay"
        role="dialog"
        aria-labelledby="activity-detail-title"
        aria-modal="true"
    >
        <button type="button" class="modal-backdrop" aria-label="Close dialog" on:click={closeDetailModal}></button>
        <div class="modal-content" role="document">
            <div class="modal-header">
                <h3 id="activity-detail-title">Activity Detail</h3>
                <button type="button" class="modal-close" on:click={closeDetailModal} aria-label="Close">×</button>
            </div>
            <div class="modal-body">
                <dl class="detail-list">
                    <dt>Event Type</dt>
                    <dd><span class="color-badge modal-badge" style="--badge-color: {getEventColor(selectedActivity.event_type)}">{getEventLabel(selectedActivity.event_type)}</span></dd>

                    <dt>Timestamp</dt>
                    <dd>{formatDate(selectedActivity.created_at)}</dd>

                    <dt>User</dt>
                    <dd>
                        {#if selectedActivity.user}
                            <a href="mailto:{selectedActivity.user.email}">{selectedActivity.user.first_name} {selectedActivity.user.last_name} ({selectedActivity.user.email})</a>
                        {:else}
                            —
                        {/if}
                    </dd>

                    {#if selectedActivity.related_entity_uuid}
                        <dt>Related</dt>
                        <dd>
                            {#if getRelatedEntityLink(selectedActivity)}
                                <a href={getRelatedEntityLink(selectedActivity) || '#'} target="_blank" rel="noopener">View Brief</a>
                            {:else}
                                {selectedActivity.related_entity_type} - {selectedActivity.related_entity_uuid}
                            {/if}
                        </dd>
                    {/if}

                    {#if selectedActivity.ip_address}
                        <dt>IP Address</dt>
                        <dd>{selectedActivity.ip_address}</dd>
                    {/if}

                    <dt>Metadata</dt>
                    <dd>
                        <pre class="metadata-block">{JSON.stringify(selectedActivity.metadata || {}, null, 2)}</pre>
                    </dd>
                </dl>
            </div>
            <div class="modal-footer">
                <button type="button" class="modal-close-btn" on:click={closeDetailModal}>Close</button>
            </div>
        </div>
    </div>
{/if}

<style>
    .summary-cell {
        max-width: 300px;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 2000;
    }

    .modal-backdrop {
        position: absolute;
        inset: 0;
        border: none;
        padding: 0;
        margin: 0;
        background: var(--shade-background-color);
        cursor: default;
    }

    .modal-content {
        position: relative;
        z-index: 1;
        background: var(--white);
        border-radius: var(--border-radius-button);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
        max-width: 640px;
        width: 92%;
        max-height: 88vh;
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }

    .modal-body {
        overflow-y: auto;
        flex: 1;
        padding: 1.5rem;
    }

    .modal-footer {
        padding: 1rem 1.5rem;
        border-top: 1px solid var(--border-color);
        background: var(--greyish);
        flex-shrink: 0;
    }

    .modal-close-btn {
        display: block;
        width: 100%;
        padding: 0.75rem 1.5rem;
        background: var(--primary);
        color: var(--white);
        border: none;
        border-radius: var(--border-radius-button);
        font-size: 1rem;
        font-weight: 500;
        cursor: pointer;
    }

    .modal-close-btn:hover {
        background: var(--darkprimary);
    }

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 1.5rem;
        border-bottom: 1px solid var(--border-color);
    }

    .modal-header h3 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 500;
        color: var(--grey);
    }

    .modal-close {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
        color: var(--grey-secondary);
        padding: 0 0.25rem;
    }

    .modal-close:hover {
        color: var(--grey);
    }

    .detail-list {
        margin: 0;
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 0.5rem 1.5rem;
    }

    .detail-list dt {
        font-weight: 500;
        color: var(--grey-secondary);
    }

    .detail-list dd {
        margin: 0;
    }

    .detail-list a {
        color: var(--primary);
        text-decoration: underline;
    }

    .metadata-block {
        background: var(--greyish);
        padding: 0.75rem;
        border-radius: 4px;
        font-size: 0.8125rem;
        overflow-x: auto;
        margin: 0;
    }
</style>
